import moment from "moment";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { determineIfTheJCodeFieldShouldBeEditable } from "./determineIfTheFieldShouldBeEditable";

export const checkIfVisitCompletedIsBlank = (
  visitCompleted: number | undefined | null,
) => {
  if (visitCompleted === undefined || visitCompleted === null) {
    return true;
  }
  return false;
};

export const checkIfVisitsRemainingIsBlank = (
  visitsRemaining: number | undefined | null,
) => {
  if (visitsRemaining === undefined || visitsRemaining === null) {
    return true;
  }
  return false;
};

export const checkIfNumVisitsIsBlank = (
  numVisits: number | undefined | null,
) => {
  if (numVisits === undefined || numVisits === null || numVisits === 0) {
    return true;
  }
  return false;
};

export const checkIfNumVisitsMatchesRemainingAndCompleted = (
  numVisits: number | undefined | null,
  visitsRemaining: number | undefined | null,
  visitsCompleted: number | undefined | null,
) => {
  if (
    numVisits === undefined ||
    numVisits === null ||
    visitsRemaining === undefined ||
    visitsRemaining === null ||
    visitsCompleted === undefined ||
    visitsCompleted === null ||
    numVisits !== visitsRemaining + visitsCompleted
  ) {
    return false;
  }
  return true;
};

export const checkIfStringIsValidNumber = (str: string | undefined | null) => {
  if (str === undefined || str === "" || isNaN(Number(str))) {
    return false;
  }
  return true;
};

export const checkIfDateIsInCorrectFormat = (
  date: string | undefined,
  format: string = "MM/DD/YYYY",
) => {
  if (
    date === undefined ||
    date === "" ||
    !moment.utc(date, format, true).isValid()
  ) {
    return false;
  }
  return true;
};

export const checkIfDateIsEarlierThanDateOfService = (
  date: string | undefined,
  dateOfService: string | undefined,
) => {
  // First check if both dates are valid
  if (
    date === undefined ||
    date === "" ||
    dateOfService === undefined ||
    dateOfService === "" ||
    !moment.utc(date, "MM/DD/YYYY", true).isValid() ||
    !moment.utc(dateOfService, "MM/DD/YYYY", true).isValid()
  ) {
    return false;
  }

  // Only compare if both dates are valid
  const result = moment
    .utc(date, "MM/DD/YYYY", true)
    .isSameOrBefore(moment.utc(dateOfService, "MM/DD/YYYY", true));
  return result;
};

export const checkIfDateIsLaterThanDateOfService = (
  date: string | undefined,
  dateOfService: string | undefined,
) => {
  // First check if both dates are valid
  if (
    date === undefined ||
    date === "" ||
    dateOfService === undefined ||
    dateOfService === "" ||
    !moment.utc(date, "MM/DD/YYYY", true).isValid() ||
    !moment.utc(dateOfService, "MM/DD/YYYY", true).isValid()
  ) {
    return false;
  }

  // Only compare if both dates are valid
  const result = moment
    .utc(date, "MM/DD/YYYY", true)
    .isSameOrAfter(moment.utc(dateOfService, "MM/DD/YYYY", true));
  return result;
};

/**
 * Returns true when `date` is strictly later than `compareTo`. Both inputs
 * must be valid `MM/DD/YYYY` strings; otherwise returns false (matches the
 * defensive behavior of the other date comparators in this module).
 */
export const checkIfDateIsAfter = (
  date: string | undefined,
  compareTo: string | undefined,
) => {
  if (
    date === undefined ||
    date === "" ||
    compareTo === undefined ||
    compareTo === "" ||
    !moment.utc(date, "MM/DD/YYYY", true).isValid() ||
    !moment.utc(compareTo, "MM/DD/YYYY", true).isValid()
  ) {
    return false;
  }

  return moment
    .utc(date, "MM/DD/YYYY", true)
    .isAfter(moment.utc(compareTo, "MM/DD/YYYY", true));
};

export const checkIfValueIsBlank = (value: string | undefined | null) => {
  if (value === undefined || value === null || value.trim() === "") {
    return true;
  }
  return false;
};

export const checkIfJCodeFieldHasError = (
  authStatus: string,
  jCode: string,
) => {
  const isJCodeBlank = checkIfValueIsBlank(jCode);
  const isAuthStatusValid = determineIfTheJCodeFieldShouldBeEditable(
    authStatus,
    jCode,
  );
  return isJCodeBlank && isAuthStatusValid;
};

/**
 * Determines whether the authorization-number field is in an error state.
 *
 * The authorization number is only mandatory (and therefore only flagged as
 * errored when blank) for statuses where an authorization is expected on
 * record — `AuthOnFile` and `AuthByRISA`. For any other auth status a blank
 * authorization number is considered valid and this returns `false`.
 *
 * @param authNumber - The authorization number entered in the cell.
 * @param authStatus - The row's current PA-required status id; only
 *   `AuthOnFile` and `AuthByRISA` require a non-blank authorization number.
 * @returns `true` when the authorization number is blank AND the status
 *   requires one; otherwise `false`.
 */
export const checkIfAuthNumberFieldHasError = (
  authNumber: string | undefined | null | "",
  authStatus: string,
) => {
  const mandatoryFieldsForAuthNumber = [
    MedicalPaOrdersAuthStatus.AuthOnFile,
    MedicalPaOrdersAuthStatus.AuthByRISA,
  ];
  const isAuthStatusValid = mandatoryFieldsForAuthNumber.includes(
    authStatus as MedicalPaOrdersAuthStatus,
  );
  return checkIfValueIsBlank(authNumber) && isAuthStatusValid;
};
